import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Grid2 } from "@mui/material";
import { axiosClient } from "../api/proxy/AxiosClient";
import ViewEditBilling from "./ViewEditBilling";
import { usePaymentService } from "../services/PaymentService";
import ActionButtonsPane from "./ActionButtonsPane";
import LessonDebtStack from "./LessonDebtStack";

const Billing = () => {

  const [mode, setMode] = useState("empty");
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentBalances, setStudentBalances] = useState([]);

  const [pastLessonDetails, setPastLessonDetails] = useState([]);
  const [pastRawLessonDetails, setPastRawLessonDetails] = useState([]);
  
  const [unattendedLessonDetails, setUnattendedLessonDetails] = useState([]);
  const [unattendedRawLessonDetails, setUnattendedRawLessonDetails] = useState([]);

  const [futureLessonDetails, setFutureLessonDetails] = useState([]);
  const [futureRawLessonDetails, setFutureRawLessonDetails] = useState([]);

  const filteredLessonIds = useSelector(state => state.filters.filteredLessonIds);

  const user = JSON.parse(localStorage.getItem("user"));

  const { openPay, openMakeDeposit } = usePaymentService();
  const actions = [
    {
      label: "Pay",
      color: "#0000FF",// blue
      active: () => !user.isDemo && selectedStudent?.lessonBalance < 0,
      handler: () => {
        console.log("Pay clicked:", selectedStudent);
        openPay(selectedStudent, selectedLesson, fetchData);
      },
    },
    {
      label: "Deposit",
      color: "#FF7F00",// orange
      active: () => !user.isDemo && selectedStudent,
      handler: () => {
        console.log("Deposit clicked:", selectedStudent);
        openMakeDeposit(user, selectedStudent, studentBalances[selectedStudent.uid], selectedLesson?.ccy, fetchData);
      },
    },
  ];

  const buildLessonDetails = (lessons, txs, statuses) => {
    const studentBalances = {};
    txs.forEach(tx => {
      if (!studentBalances[tx.debtor.uid]) {
        studentBalances[tx.debtor.uid] = {};
      }
      if (!studentBalances[tx.debtor.uid][tx.ccy]) {
        studentBalances[tx.debtor.uid][tx.ccy] = 0;
      }

      let vector = 0;
      if (tx.type === 'deposit') vector = 1;
      else if (tx.type === 'payment') vector = -1;
      studentBalances[tx.debtor.uid][tx.ccy] += (vector * tx.amount);
    });
    setStudentBalances(studentBalances);

    return lessons
      .filter (lesson => statuses.includes(lesson.status))
      .map((lesson) => {
        const lessonTxs = txs.filter(tx => tx.lesson?.uid === lesson.uid);
        const lessonStudents = lesson.students.map(s => ({name: s.firstName + ' ' + s.lastName, uid: s.uid }));

        const students = lessonStudents.map(student => {
          const studentTxs = lessonTxs.filter(tx => tx.debtor.uid === student.uid);
          const studentPayments = studentTxs.filter(tx => tx.type === 'payment');
          const fee = lesson.fee;

          const paid = studentPayments.reduce((sum, tx) => sum + tx.amount, 0);
          const discount = studentPayments.reduce((sum, tx) => sum + (tx.discount || 0), 0);
          const deposit = studentBalances?.[student.uid]?.[lesson.ccy] || 0;

          const lessonBalance = paid + discount - fee;

          return {
            uid: student.uid,
            name: student.name,
            lessonUid: lesson.uid,
            fee,
            paid,
            discount,
            deposit,
            lessonBalance
          };
        });

        const totalFee = lesson.fee * students.length;
        const totalPaid = students.reduce((sum, s) => sum + s.paid, 0);
        const totalDiscount = students.reduce((sum, s) => sum + s.discount, 0);

        const balance = totalPaid + totalDiscount - totalFee;

        return {
          uid: lesson.uid,
          status: lesson.status,
          startDttm: lesson.startDttm,
          endDttm: lesson.endDttm,
          name: lesson.name,
          fee: lesson.fee,
          ccy: lesson.ccy,
          students,
          balance
        };
    })
    .filter(lesson => lesson.balance !== 0);
  }

  const filterLessons = useCallback((lessonDetails) => {
    if (filteredLessonIds.length > 0) {
      return lessonDetails.filter(lesson => filteredLessonIds.includes(lesson.uid));
    }
    return lessonDetails;
  }, [filteredLessonIds]);

  useEffect(() => {
    if (!pastRawLessonDetails   || !pastRawLessonDetails.length ||
        !futureRawLessonDetails || !futureRawLessonDetails.length) {
          return;
        }
    if (selectedLesson) {
      const rawsDetails = ['submitted', 'confirmed'].includes(selectedLesson.status) ?
        ['finished', 'billed'].includes(selectedLesson.status) ? 
          pastRawLessonDetails :
          futureRawLessonDetails :
        [];

      const updatedLesson = rawsDetails.find(l => l.uid === selectedLesson.uid);
      if (updatedLesson) {
        setSelectedLesson(updatedLesson);
        if (selectedStudent) {
          const updatedStudent = updatedLesson?.students.find((s) => s.uid === selectedStudent.uid);
          if (updatedStudent) {
            setSelectedStudent(updatedStudent);
          }
        } else {
          setSelectedStudent(updatedLesson.students[0]);
        }
      }
    }
  }, [futureRawLessonDetails, pastRawLessonDetails, selectedLesson, selectedStudent]);

  const fetchData = useCallback(async () => {
    try {
      const [lessonsResponse, paymentsResponse] = await Promise.all([
        axiosClient.get(`/lessons?${user.type}Uid=${user.uid}`),
        axiosClient.get(`/payments/by-teacher/${user.uid}`),
      ]);
  
      const lessons = lessonsResponse?.data?.sort((a, b) => new Date(b.startDttm) - new Date(a.startDttm));
      
      const pastDebtLessonDetails = buildLessonDetails(
        lessons,
        paymentsResponse.data,
        ['finished', 'billed']
      );
      setPastLessonDetails(filterLessons(pastDebtLessonDetails));
      setPastRawLessonDetails(pastRawLessonDetails);
      
      const futureDebtLessonDetails = buildLessonDetails(
        lessons,
        paymentsResponse.data,
        ['confirmed', 'submitted']
      );
      setFutureLessonDetails(filterLessons(futureDebtLessonDetails));
      setFutureRawLessonDetails(futureRawLessonDetails);
      
      const unattendedDebtLessonDetails = buildLessonDetails(
        lessons,
        paymentsResponse.data,
        ['rescheduled', 'requested', 'proposed', 'moved']
      );
      setUnattendedLessonDetails(filterLessons(unattendedDebtLessonDetails));
      setUnattendedRawLessonDetails(unattendedRawLessonDetails);
    } catch (err) {
      console.error("Error fetching data", err);
    }
  }, [user.uid, user.type, filterLessons, pastRawLessonDetails, futureRawLessonDetails, unattendedRawLessonDetails]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedLesson && mode !== "view") {
      setMode("view");
    } else if (!selectedLesson && mode === "view") {
      setMode("empty");
    }
  }, [selectedLesson, mode]);

  return (
    <Box p={3}>
      <Grid2 container spacing={2} justifyContent="center">
      {/* <GroupedLessonList
        title="Past debt lessons"
        lessons={pastLessonDetails}
        selectedLesson={selectedLesson}
        setSelectedLesson={setSelectedLesson}
      /> */}

      <LessonDebtStack
        pastLessons={pastLessonDetails}
        futureLessons={futureLessonDetails}
        unattendedLessons={unattendedLessonDetails}
        selectedLesson={selectedLesson}
        setSelectedLesson={setSelectedLesson}
      />

      <Grid2>
        {mode === "view" && selectedLesson ? (
          <ViewEditBilling 
            lesson={selectedLesson}
            selectedStudent={selectedStudent}
            setSelectedStudent={setSelectedStudent}
          />
        ) : (
          <Box
            sx={{
              border: "2px dashed #b0b0b0",
              borderRadius: 2,
              height: "100%",
              minHeight: "400px",
              width: "620px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "gray",
              bgcolor: "#fafafa",
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Select Lesson
            </Typography>
          </Box>
        )}
      </Grid2>

      <ActionButtonsPane
        actions={actions}
      />

    {/* <GroupedLessonList
      title="Future debt lessons"
      lessons={futureLessonDetails}
      selectedLesson={selectedLesson}
      setSelectedLesson={setSelectedLesson}
    />

    <GroupedLessonList
      title="Unattended Lessons"
      lessons={unattendedLessonDetails}
      selectedLesson={selectedLesson}
      setSelectedLesson={setSelectedLesson}
    /> */}

      </Grid2>
    </Box>
  );
};

export default Billing;
